import React, { useEffect, useState, useCallback, useRef } from 'react';
import { debounce } from '@wordpress/compose';
import { MasonryGrid } from "@egjs/react-grid";
import { createRoot } from "react-dom/client";
import { createPortal } from 'react-dom';

interface GalleryTag {
	slug: string;
	name: string;
	id: number;
	}

interface GalleryImage {
	id: string;
	src: string;
	fullSrc?: string;
	alt?: string;
	tags: string[];
	caption?: string;
	srcset?: string;
	width?: number;
	height?: number;
	fullWidth?: number;
	fullHeight?: number;
	lightboxSrc?: string;
	}

interface GalleryViewProps {
	perPage?: number;
	roottaxonomy?: string;
	rootterm?: string;
	maxColumns?: number;
	lightboxsize?: string;
	imageRadius?: number;
	lightboxBg?: string;
	captionColor?: string;
	}

const DEFAULT_COLS = 3;
const masonryGap = 16;

const stripHtml = (value?: string) =>
	value ? value.replace(/<[^>]*>/g, '').trim() : '';

const ASPECT_TOLERANCE = 0.01;

const ratiosMatch = (baseWidth?: number, baseHeight?: number, candidateWidth?: number, candidateHeight?: number) => {
	const baseRatio = baseWidth && baseHeight ? baseWidth / baseHeight : null;
	const candidateRatio = candidateWidth && candidateHeight ? candidateWidth / candidateHeight : null;
	if (!baseRatio || !candidateRatio) return true;
	return Math.abs(baseRatio - candidateRatio) <= ASPECT_TOLERANCE;
	};

const buildSrcSet = (mediaDetails?: any) => {
	if (!mediaDetails?.sizes) return '';
	const baseWidth = mediaDetails?.width;
	const baseHeight = mediaDetails?.height;
	const entries = Object.values(mediaDetails.sizes) as Array<{ source_url?: string; width?: number; height?: number }>;
	const safeEntries = entries.filter((size) => size?.source_url && size?.width && ratiosMatch(baseWidth, baseHeight, size.width, size.height));
	if (!safeEntries.length) return '';
	return safeEntries
		.sort((a, b) => (a.width || 0) - (b.width || 0))
		.map((size) => `${size.source_url} ${size.width}w`)
		.join(', ');
	};

const pickDisplaySource = (mediaDetails?: any, fallback?: string) => {
	const baseWidth = mediaDetails?.width;
	const baseHeight = mediaDetails?.height;
	const buildResult = (size?: any) => ({
		src: size?.source_url || fallback,
		width: size?.width || baseWidth,
		height: size?.height || baseHeight,
		});
	const isAspectSafe = (size?: any) => {
		if (!size?.source_url) return false;
		return ratiosMatch(baseWidth, baseHeight, size.width, size.height);
		};
	if (!mediaDetails?.sizes) {
		return buildResult();
		}
	const preferredOrder = ['large', 'medium_large', 'medium'];
	for (const key of preferredOrder) {
		const size = mediaDetails.sizes?.[key];
		if (isAspectSafe(size)) return buildResult(size);
		}
	const firstSize = Object.values(mediaDetails.sizes).find((size: any) => isAspectSafe(size));
	if (firstSize) return buildResult(firstSize as any);
	return buildResult();
	}


export const GalleryView: React.FC<GalleryViewProps> = ({ perPage = 12, roottaxonomy, rootterm, maxColumns, lightboxsize, imageRadius, lightboxBg, captionColor }) => {
	// State for child tags
	const [childTags, setChildTags] = useState<GalleryTag[]>([]);
	// State for root term ID
	const [rootTermId, setRootTermId] = useState<number | null>(null);
	// State for images
	const [images, setImages] = useState<GalleryImage[]>([]);
	// State for error
	const [error, setError] = useState<string | null>(null);
	// State for loading
	const [loading, setLoading] = useState(false);
	// Track last tag query
	const lastTagRef = useRef<string[] | undefined>(undefined);
	// Pagination state
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	// Multi-tag selection state
	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	// Ready state
	const [ready, setReady] = useState(false);
	// Responsive columns
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [columnCount, setColumnCount] = useState(() => Math.max(1, maxColumns || DEFAULT_COLS));
	const [containerWidth, setContainerWidth] = useState(0);
	const [lightboxImage, setLightboxImage] = useState<GalleryImage | null>(null);

	useEffect(() => {
		if (!lightboxImage) return;
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape')
				setLightboxImage(null);
			}
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
		}, [lightboxImage]);

	useEffect(() => {
		if (!lightboxImage) return;
		document.body.classList.add('vg-lightbox-open');
		return () => document.body.classList.remove('vg-lightbox-open');
		}, [lightboxImage]);

	// Reset error on any data call
	const resetError = () => setError(null);

	// Fetch child tags
	// Lookup root term ID from taxonomy and slug
	useEffect(() => {
		resetError();
		setRootTermId(null);
		setReady(false);
		if (!roottaxonomy || !rootterm) return;
		const url = `/wp-json/wp/v2/${roottaxonomy}?slug=${encodeURIComponent(rootterm)}&per_page=1`;
		fetch(url)
			.then(res => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return res.json();
				})
			.then((terms) => {
				if (terms && terms.length > 0) {
					setRootTermId(terms[0].id);
					setReady(true);
				} else
					setError('Root term not found');
				})
			.catch(e => {
				setError('Failed to load root term');
				console.error('VG-Gallery: Error loading root term (REST)', { error: e, roottaxonomy, rootterm, url });
				});
		}, [roottaxonomy, rootterm]);

	// Fetch child tags when rootTermId is available
	useEffect(() => {
		resetError();
		if (!roottaxonomy || !rootTermId) return;
		const url = `/wp-json/wp/v2/${roottaxonomy}?parent=${rootTermId}&per_page=100`;
		fetch(url)
			.then(res => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return res.json();
				})
			.then((terms) => {
				setChildTags((terms || []).map((t: any) => ({ slug: t.slug, name: t.name, id: t.id })));
				})
			.catch(e => {
				setError('Failed to load tags');
				console.error('VG-Gallery: Error loading tags (REST)', { error: e, roottaxonomy, rootTermId, url });
				});
		}, [roottaxonomy, rootTermId]);

	// Debounced fetch for images using wp.compose debounce
	const calculateColumns = useCallback((width: number) => {
		const maxCols = Math.max(1, maxColumns || DEFAULT_COLS);
		if (width <= 0) return maxCols;
		if (width < 650) {
			const ratio = width / 650;
			const scaled = Math.max(1, Math.floor(ratio * maxCols));
			return scaled;
			}
		return maxCols;
		}, [maxColumns]);

	useEffect(() => {
		const node = containerRef.current;
		if (!node) return;
		const updateColumns = (width?: number) => 
			{
			const currentWidth = width ?? node.getBoundingClientRect().width;
			setContainerWidth(currentWidth);
			const next = calculateColumns(currentWidth);
			setColumnCount(prev => (prev !== next ? next : prev));
			};
		updateColumns();
		if (typeof ResizeObserver !== 'undefined') 
			{
			const observer = new ResizeObserver(entries => {
				if (!entries || !entries.length) return;
				updateColumns(entries[0].contentRect.width);
				});
			observer.observe(node);
			return () => observer.disconnect();
			}
		const handleResize = () => updateColumns();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
		}, [calculateColumns]);

	const fetchImages = useCallback(
		debounce((tags: string[] = [], pageNum: number = 1, append: boolean = false) => {
			resetError();
			setLoading(true);
			if (!rootTermId) {
				setLoading(false);
				return;
				}
			// Hierarchical query: images in root and children
			// Collect all term IDs: root + children
			if (!roottaxonomy) {
				setLoading(false);
				return;
				}
			const childIds = childTags.map(t => t.id);
			let termIds = tags.length === 0
				? childIds
				: childTags.filter(tag => tags.includes(tag.slug)).map(tag => tag.id);
			if (!termIds.length && rootTermId) {
				termIds = [rootTermId];
				}
			if (!termIds.length) {
				setLoading(false);
				return;
				}
			let url = `/wp-json/wp/v2/media?per_page=${perPage}&page=${pageNum}&${roottaxonomy}=${termIds.join(',')}`;
			fetch(url)
				.then(res => {
					if (!res.ok) throw new Error(`HTTP ${res.status}`);
					return res.json();
					})
				.then((posts) => 
					{
					const newImages = (posts || []).map((p: any) => {
						const mediaDetails = p.media_details;
						const { src, width, height } = pickDisplaySource(mediaDetails, p.source_url);
						let lightboxSrc = p.source_url;
						if (lightboxsize && lightboxsize !== 'full') {
							const candidate = mediaDetails?.sizes?.[lightboxsize];
							if (candidate?.source_url)
								lightboxSrc = candidate.source_url;
							}
						return {
							id: p.id,
							src,
							fullSrc: p.source_url,
							alt: p.alt_text,
							tags: p.tags || [],
							caption: stripHtml(p.caption?.rendered),
							srcset: buildSrcSet(mediaDetails),
							width,
							height,
							fullWidth: mediaDetails?.width,
							fullHeight: mediaDetails?.height,
							lightboxSrc,
							};
						});
					setImages(prev => append && pageNum > 1 ? [...prev, ...newImages] : newImages);
					setHasMore(newImages.length === perPage);
					})
				.catch(e => {
					setError('Failed to load images');
					console.error('VG-Gallery: Error loading images (REST)', { error: e, tags, pageNum, append, perPage, url });
					})
				.finally(() => setLoading(false));
			}, 300),
		[perPage, rootTermId, roottaxonomy, childTags, lightboxsize]
		)

	// Initial load and on tag change
	useEffect(() => {
		if (!ready) return;
		setPage(1);
		fetchImages(selectedTags, 1, false);
		lastTagRef.current = selectedTags;
		// Cleanup debounce on unmount
		return () => { fetchImages.cancel(); };
		}, [selectedTags, perPage, rootTermId, roottaxonomy, ready, lightboxsize]);

	// Load more handler
	const handleLoadMore = () => {
		const nextPage = page + 1;
		setPage(nextPage);
		fetchImages(selectedTags, nextPage, true);
		}

	// Tag click handler
	const handleTagClick = (slug: string) => 
		{
		if (slug === '__all__')
			setSelectedTags([]);
		else
			setSelectedTags(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);
		}

	const handleLightboxClose = useCallback(() => setLightboxImage(null), []);
	const handleItemActivate = useCallback((img: GalleryImage) => setLightboxImage(img), []);

	const galleryStyle = {
		['--vg-gallery-columns' as any]: columnCount.toString(),
	} as React.CSSProperties;
	if (typeof imageRadius === 'number') {
		galleryStyle['--vg-gallery-radius' as any] = `${imageRadius}px`;
	}
	if (lightboxBg) {
		galleryStyle['--vg-gallery-lightbox-bg' as any] = lightboxBg;
	}
	if (captionColor) {
		galleryStyle['--vg-gallery-caption-text' as any] = captionColor;
	}
	const columnWidth = columnCount > 0 ? (containerWidth - (columnCount - 1) * masonryGap) / columnCount : containerWidth;
	const imageSizesAttr = columnWidth > 0 ? `${Math.round(columnWidth)}px` : undefined;
	const lightboxStyle: React.CSSProperties = {};
	if (lightboxBg) {
		lightboxStyle['--vg-gallery-lightbox-bg' as any] = lightboxBg;
	}
	if (captionColor) {
		lightboxStyle['--vg-gallery-caption-text' as any] = captionColor;
	}
	if (typeof imageRadius === 'number') {
		lightboxStyle['--vg-gallery-radius' as any] = `${imageRadius}px`;
	}
	const lightboxNode = lightboxImage ? createPortal(
		<div
			className="vg-lightbox"
			role="dialog"
			aria-modal="true"
			aria-label={lightboxImage.alt || lightboxImage.caption || 'Enlarged image'}
			onClick={handleLightboxClose}
			style={lightboxStyle}
		>
			<div className="vg-lightbox-backdrop" aria-hidden="true"></div>
			<div className="vg-lightbox-body" onClick={event => event.stopPropagation()}>
				<button type="button" className="vg-lightbox-close" aria-label="Close" onClick={handleLightboxClose}>×</button>
				<img
					src={lightboxImage.lightboxSrc || lightboxImage.fullSrc || lightboxImage.src}
					alt={lightboxImage.alt || ''}
					srcSet={lightboxImage.srcset || undefined}
					sizes="100vw"
					loading="eager"
				/>
				{lightboxImage.caption && (
					<div className="vg-lightbox-caption">{lightboxImage.caption}</div>
				)}
			</div>
		</div>,
		document.body
	) : null;

	return (
		<>
		<div ref={containerRef} className="vg-gallery-block" style={galleryStyle}>
			{(!ready || loading) && <div className="vg-gallery-loading">Loading...</div>}
			{error && <div className="vg-gallery-error">{error}</div>}
			{ready && (
				<>
				<div className="vg-gallery-tags">
					<a
						rel="tag"
						key="__all__"
						href="#"
						onClick={e => {
							e.preventDefault();
							handleTagClick('__all__');
						}}
						className={selectedTags.length === 0 ? 'is-selected' : ''}
					>
						All
					</a>
					{childTags.map(tag => (
						<a
							rel="tag"
							key={tag.slug}
							href="#"
							onClick={e => {
								e.preventDefault();
								handleTagClick(tag.slug);
							}}
							className={selectedTags.includes(tag.slug) ? 'is-selected' : ''}
						>
							{tag.name}
						</a>
					))}
				</div>
				<MasonryGrid
					gap={masonryGap}
					column={columnCount}
					defaultDirection="end"
					className="vg-gallery-masonry"
					>
					{images.map(img => (
						<div key={img.id} className="vg-gallery-item">
							<button
								type="button"
								className="vg-gallery-trigger"
								onClick={() => handleItemActivate(img)}
								aria-label={img.alt || img.caption || 'View image'}
							>
								<img
									src={img.src}
									alt={img.alt || ''}
									decoding="async"
									srcSet={img.srcset || undefined}
									sizes={img.srcset && imageSizesAttr ? imageSizesAttr : undefined}
								/>
								{img.caption && (
									<div className="vg-gallery-caption">
										{img.caption}
									</div>
								)}
							</button>
						</div>
					))}
				</MasonryGrid>
				{!loading && hasMore && (
					<div className="vg-gallery-loadmore-wrap wp-block-button">
						<button type="button" className="wp-block-button__link" onClick={handleLoadMore}>
							Load More
						</button>
					</div>
				)}
				</>
			)}
		</div>
		{lightboxNode}
		</>
	);
};


function getAttr(el, name, fallback) {
  return el.getAttribute(name) || fallback;
	}


document.addEventListener("DOMContentLoaded", () => 
	{
	if (!document.body.classList.contains("wpfront")) return;
	document.querySelectorAll(".wp-block-blox-vg-gallery").forEach((el) => 
  		{
		const roottaxonomy = getAttr(el, "data-roottaxonomy", "");
		const rootterm = getAttr(el, "data-rootterm", "");
		const perPage = parseInt(getAttr(el, "data-perpage", "12"), 10);
		const maxColumns = parseInt(getAttr(el, "data-maxcolumns", "3"), 10);
		const lightboxsize = getAttr(el, "data-lightboxsize", "full");
		const imageRadiusAttr = getAttr(el, "data-image-radius", "");
		const imageRadius = imageRadiusAttr ? parseInt(imageRadiusAttr, 10) : undefined;
		const lightboxBg = getAttr(el, "data-lightbox-bg", "");
		const captionColor = getAttr(el, "data-caption-color", "");
		const root = createRoot(el);
		root.render(
			<GalleryView
				roottaxonomy={roottaxonomy}
				rootterm={rootterm}
				perPage={perPage}
				maxColumns={maxColumns}
				lightboxsize={lightboxsize}
				imageRadius={imageRadius}
				lightboxBg={lightboxBg || undefined}
				captionColor={captionColor || undefined}
			/>)
		console.log('VG-Gallery: Initialized gallery', { roottaxonomy, rootterm, perPage, maxColumns, lightboxsize, imageRadius, lightboxBg, captionColor, element: el });
		})
	})
